import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogCancelButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Form } from '@/components/ui-kit/form';
import { EMAIL_PATTERN } from '@/modules/service-requests/shared/application-utils';
import { fetchEmployeeSnapshot } from '@/modules/service-requests/shared/employee';
import { translatedResolver } from '@/modules/service-requests/shared/form-resolver';
import {
  FieldGrid,
  TextareaField,
  TextField,
} from '@/modules/service-requests/shared/components/form-fields';
import { useGenerateNoc } from '../../hooks/use-noc';
import { DEFAULT_POINT_OF_CONTACT, renderNocLetterBody } from '../../utils/noc-rules';
import { NocApplication, NocType } from '../../types/noc.types';

const required = (key: string) => z.string().trim().min(1, { message: key });

const docInfoSchema = z.object({
  documentNumber: required('NOC_NUMBER_REQUIRED').max(100, { message: 'TEXT_TOO_LONG' }),
  letterBody: required('NOC_BODY_REQUIRED').max(20000, { message: 'TEXT_TOO_LONG' }),
  pocName: required('POC_NAME_REQUIRED').max(150, { message: 'TEXT_TOO_LONG' }),
  pocDesignation: required('POC_DESIGNATION_REQUIRED').max(150, { message: 'TEXT_TOO_LONG' }),
  pocEmail: required('POC_EMAIL_REQUIRED').regex(EMAIL_PATTERN, { message: 'EMAIL_INVALID' }),
  // The workflow's phone pattern: digits with optional +, spaces, brackets and dashes.
  pocMobile: required('POC_MOBILE_REQUIRED').regex(/^\+?[0-9][0-9 ()-]{5,24}$/, {
    message: 'PHONE_NUMBER_INVALID',
  }),
});
type DocInfoValues = z.infer<typeof docInfoSchema>;

interface NocDocInfoDialogProps {
  application: NocApplication | null;
  nocTypes: NocType[];
  onOpenChange: (open: boolean) => void;
  /** Called once the letter is generated, so the caller can open the report. */
  onGenerated: (application: NocApplication) => void;
}

/**
 * "Information for NOC document": the reviewer confirms the reference number, the letter text
 * (pre-filled from the NOC type's template) and the point of contact, then generates the letter.
 */
export const NocDocInfoDialog = ({
  application,
  nocTypes,
  onOpenChange,
  onGenerated,
}: NocDocInfoDialogProps) => {
  const { t } = useTranslation();
  const { mutateAsync: generate, isPending } = useGenerateNoc();

  // The letter mentions the applicant's joining date and uses their pronouns.
  const applicantId = application?.UserId;
  const { data: applicant } = useGlobalQuery({
    queryKey: ['hr-service-desk-employee', applicantId],
    queryFn: () => fetchEmployeeSnapshot(applicantId as string),
    enabled: Boolean(applicantId),
    staleTime: 5 * 60 * 1000,
  });

  const form = useForm<DocInfoValues>({
    resolver: translatedResolver(docInfoSchema, t),
    defaultValues: { documentNumber: '', letterBody: '', ...DEFAULT_POINT_OF_CONTACT },
  });

  // A new application in the dialog starts from a clean form.
  useEffect(() => {
    if (!application) return;
    form.reset({
      documentNumber: application.DocumentNumber ?? '',
      letterBody: '',
      ...DEFAULT_POINT_OF_CONTACT,
    });
  }, [application, form]);

  // The letter is filled from the type's template once the types and the applicant have loaded;
  // only the letter, and only until the reviewer edits it, so nothing they typed is overwritten.
  const template = nocTypes.find((type) => type.ItemId === application?.NocTypeId)?.DocumentBody;
  useEffect(() => {
    if (!application || form.getFieldState('letterBody').isDirty) return;
    form.setValue(
      'letterBody',
      renderNocLetterBody(template, {
        application,
        joiningDate: applicant?.joiningDate,
        sex: applicant?.sex,
      })
    );
  }, [application, template, applicant, form]);

  // Once generated, the reference number is fixed for every later re-generation.
  const numberLocked = application?.Status === 'Generated';

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!application) return;
    try {
      await generate({
        itemId: application.ItemId,
        data: {
          DocumentNumber: values.documentNumber.trim(),
          LetterBody: values.letterBody,
          PocName: values.pocName.trim(),
          PocDesignation: values.pocDesignation.trim(),
          PocEmail: values.pocEmail.trim(),
          PocMobile: values.pocMobile.trim(),
        },
      });
      onGenerated(application);
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  return (
    <Dialog open={Boolean(application)} onOpenChange={onOpenChange}>
      <DialogContent
        alignCloseWithTitle
        className="grid-cols-[minmax(0,1fr)] max-h-[90dvh] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader className="pr-6">
          <DialogTitle className="break-words">{t('INFORMATION_FOR_NOC_DOCUMENT')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextField
              control={form.control}
              name="documentNumber"
              required
              label={t('NOC_NUMBER')}
              placeholder={t('TYPE_NOC_NUMBER')}
              disabled={numberLocked}
            />
            <TextareaField
              control={form.control}
              name="letterBody"
              required
              label={t('NOC_BODY')}
              rows={8}
            />
            <FieldGrid>
              <TextField control={form.control} name="pocName" required label={t('POC_NAME')} />
              <TextField
                control={form.control}
                name="pocDesignation"
                required
                label={t('POC_DESIGNATION')}
              />
              <TextField
                control={form.control}
                name="pocEmail"
                required
                type="email"
                label={t('POC_EMAIL')}
              />
              <TextField
                control={form.control}
                name="pocMobile"
                required
                type="tel"
                label={t('POC_MOBILE')}
              />
            </FieldGrid>
            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('GENERATE_NOC')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
